import React, { Fragment, useEffect, useMemo, useState } from 'react';

import { format } from 'date-fns';

import { formatFigure } from 'Helpers/formatService';

import Button from 'CommonComponents/Button';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import TablePagination from 'CommonComponents/TablePagination';
import TableSearchInput from 'CommonComponents/TableSearchInput';
import TableSortHeaderCell from 'CommonComponents/TableSortHeaderCell';
import TextButton from 'CommonComponents/TextButton';
import ToggleInput from 'CommonComponents/ToggleInput';

import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import usePagination from 'Hooks/usePagination';

import ChevronLeftIcon from 'Assets/icons/chevron-left.svg';
import ChevronRightIcon from 'Assets/icons/chevron-right.svg';
import CircleCheckIcon from 'Assets/icons/circle-check.svg';
import CircleXMarkIcon from 'Assets/icons/circle-xmark.svg';
import TrashIcon from 'Assets/icons/trash.svg';

export default function OrganisationEventsTable(props) {
    const [showModules, setShowModules] = useState(false);
    const [showRoles, setShowRoles] = useState(false);
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const [order, setOrder] = useState('asc');

    const pagination = usePagination({ perPage: 10 });

    const rolesNames = useMemo(() => props.roles.map((role) => role.name), [props.roles]);

    useEffect(() => {
        (async () => {
            const paginationData = await props.fetchEvents(pagination.perPage, pagination.page, sortBy, order, search);

            pagination.setTo(paginationData.meta.to);
            pagination.setFrom(paginationData.meta.from);
            pagination.setTotal(paginationData.meta.total);
            pagination.setLastPage(paginationData.meta.last_page);
        })();
    }, [pagination.page, sortBy, order, search]);

    const confirm = useConfirmationModal({
        message: $t('Are you sure you want to delete this event?'),
        warning: $t('this action cannot be reversed and the event cannot be recovered. Do you wish to continue?'),
    });

    const toggleSortBy = (field) => {
        let sortVal = sortBy;
        let orderVal = order;

        if (field === sortBy) {
            orderVal = order === 'asc' ? 'desc' : 'asc';
        } else {
            sortVal = field;
            orderVal = 'asc';
        }

        setSortBy(sortVal);
        setOrder(orderVal);
    };

    const handleSearchKeyPress = (event) => {
        if (event.key === 'Enter') {
            if (pagination.page !== 1) {
                pagination.setPage(1);
            }

            setSearch(event.target.value);
        }
    };

    const pageChange = (page) => {
        pagination.setPage(page.selected + 1);
    };

    const handleDelete = (event) => {
        confirm({
            title: $t('Deleting Event - {{event_name}}', { event_name: event.name }),
            onConfirm: () => {
                props.deleteEventBySlug(event.slug).then(() => {
                    props.fetchEvents(pagination.perPage, pagination.page);
                });
            },
        });
    };

    return (
        <div className="table__card">
            <div className="tw-flex tw-justify-between tw-mb-6">
                <div>
                    <h4 className="table__header">{$t('Projects')}</h4>
                    <div className="tw-flex tw-items-center tw-space-x-3">
                        <PaginationInfo to={pagination.to} from={pagination.from} total={pagination.total} entries={'events'} />
                        <TablePagination lastPage={pagination.lastPage} pageChange={pageChange} forcePage={pagination.page - 1} />
                    </div>
                </div>
                <div className="tw-flex tw-items-center tw-gap-x-3">
                    <TableSearchInput placeholder={$t('Search events by name')} handleKeyPress={handleSearchKeyPress} />
                    <Button as="a" variant="primary" href={props.organisationEventsCreateUrl}>
                        {$t('Create New Project')}
                    </Button>
                    <Button as="a" variant="primary" href={props.organisationEventsCloneUrl}>
                        {$t('Clone Project')}
                    </Button>
                </div>
            </div>

            <div className="inbud-table_wrapper">
                <table className="inbud-table">
                    <thead>
                        <tr>
                            <th className="tw-min-w-44" onClick={() => toggleSortBy('name')}>
                                <TableSortHeaderCell asc={sortBy === 'name' && order === 'asc'} desc={sortBy === 'name' && order === 'desc'}>
                                    {$t('Name')}
                                </TableSortHeaderCell>
                            </th>
                            <th className="tw-w-28 tw-min-w-28 tw-max-w-28">{$t('Start date')}</th>
                            <th className="tw-w-28 tw-min-w-28 tw-max-w-28">{$t('End date')}</th>
                            <th className="tw-w-28 tw-min-w-28 tw-max-w-28">{$t('Currency')}</th>
                            {!props.canViewEventDetails && <th className="tw-w-28 tw-min-w-28 tw-max-w-28">{$t('Status')}</th>}
                            {props.canViewEventDetails && (
                                <Fragment>
                                    <th className="tw-w-28 tw-min-w-28 tw-max-w-28">{$t('Revenue')}</th>
                                    <th className="tw-w-28 tw-min-w-28 tw-max-w-28">{$t('Expenditure')}</th>
                                    <th className="tw-w-28 tw-min-w-28 tw-max-w-28">{$t('Net')}</th>
                                    <th className="tw-w-28 tw-min-w-28 tw-max-w-28">
                                        <div className="tw-h-full tw-flex tw-items-center tw-justify-between tw-text-sm tw-font-semibold">
                                            <div>{$t('Modules')}</div>
                                            <button onClick={() => setShowModules((show) => !show)}>
                                                {showModules ? (
                                                    <ChevronLeftIcon className="tw-h-1.5 tw-w-1.5 tw-text-charcoal" />
                                                ) : (
                                                    <ChevronRightIcon className="tw-h-1.5 tw-w-1.5 tw-text-charcoal" />
                                                )}
                                            </button>
                                        </div>
                                    </th>
                                    {showModules &&
                                        props.organisation.subscription_plan.modules.map((module) => (
                                            <th key={module.code} className="tw-w-28 tw-min-w-28 tw-max-w-28">
                                                {module.name}
                                            </th>
                                        ))}
                                    <th className="tw-w-28 tw-min-w-28 tw-max-w-28">
                                        <div className="tw-h-full tw-flex tw-items-center tw-justify-between tw-text-sm tw-font-semibold">
                                            <div>{$t('Users')}</div>

                                            <button onClick={() => setShowRoles((show) => !show)}>
                                                {showRoles ? (
                                                    <ChevronLeftIcon className="tw-h-1.5 tw-w-1.5 tw-text-charcoal" />
                                                ) : (
                                                    <ChevronRightIcon className="tw-h-1.5 tw-w-1.5 tw-text-charcoal" />
                                                )}
                                            </button>
                                        </div>
                                    </th>
                                    {showRoles &&
                                        rolesNames.map((roleName) => (
                                            <th key={roleName} className="tw-w-28 tw-min-w-28 tw-max-w-28">
                                                {roleName}
                                            </th>
                                        ))}
                                    <th className="tw-w-28 tw-min-w-28 tw-max-w-28">{$t('Status')}</th>
                                    <th className="tw-w-28 tw-min-w-28 tw-max-w-28">{$t('Rate card')}</th>
                                    <th className="tw-w-28 tw-min-w-28 tw-max-w-28">{$t('Actions')}</th>
                                </Fragment>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {props.events.map((event) => (
                            <EventRow
                                key={event.id}
                                organisation={props.organisation}
                                event={event}
                                rolesNames={rolesNames}
                                showModules={showModules}
                                showRoles={showRoles}
                                eventDashboardUrl={props.eventDashboardUrl}
                                handleDelete={handleDelete}
                                canViewDetails={props.canViewEventDetails}
                                setRateCardEnabled={props.setRateCardEnabled}
                                setProjectClosed={props.setProjectClosed}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function EventRow({
    organisation,
    event,
    rolesNames,
    showModules,
    showRoles,
    handleDelete,
    eventDashboardUrl,
    canViewDetails,
    setRateCardEnabled,
    setProjectClosed,
}) {
    const [currentRole, setCurrentRole] = useState(null);
    // Optimistic, so the switch moves under the finger rather than after the
    // round trip; the parent puts it back if the save is refused.
    const [rateCardSaving, setRateCardSaving] = useState(false);
    const [statusSaving, setStatusSaving] = useState(false);

    const currentUsers = useMemo(() => (currentRole ? event.users[currentRole]?.users || [] : []), [event.users, currentRole]);

    function handleUserClick(roleName) {
        setCurrentRole((prevRole) => (prevRole === roleName ? null : roleName));
    }

    return (
        <React.Fragment>
            <tr key={event.id}>
                <td>
                    <a href={eventDashboardUrl.replace('EVENT_SLUG', event.slug)}>{event.name}</a>
                </td>
                <td className="inbud-date-cell">{format(new Date(event.start_date), 'dd-MM-yyyy')}</td>
                <td className="inbud-date-cell">{format(new Date(event.end_date), 'dd-MM-yyyy')}</td>
                <td>{event.currency_code}</td>
                {!canViewDetails && <td>{event.is_closed ? $t('Closed') : $t('Active')}</td>}
                {canViewDetails && (
                    <Fragment>
                        <td className="inbud-numeric-cell">{formatFigure(event.revenue)}</td>
                        <td className="inbud-numeric-cell">{formatFigure(event.expenditure)}</td>
                        <td className="inbud-numeric-cell">{formatFigure(event.net)}</td>
                        <td className="inbud-numeric-cell">{formatFigure(event.enabled_modules_count, String)}</td>
                        {showModules &&
                            organisation.subscription_plan.modules.map((module) => (
                                <td key={module.code}>
                                    <div className="tw-flex tw-items-center tw-justify-center">
                                        {event.enabled_modules[module.code] ? (
                                            <CircleCheckIcon className="tw-min-w-4 tw-h-4 tw-text-green" />
                                        ) : (
                                            <CircleXMarkIcon className="tw-min-w-4 tw-h-4 tw-text-red" />
                                        )}
                                    </div>
                                </td>
                            ))}
                        <td className="inbud-numeric-cell">{formatFigure(event.users_count, String)}</td>
                        {showRoles &&
                            rolesNames.map((roleName) => (
                                <td
                                    className="inbud-numeric-cell tw-cursor-pointer tw-text-blue-75 hover:tw-underline"
                                    key={roleName}
                                    onClick={() => handleUserClick(roleName)}
                                >
                                    {event.users[roleName]?.users?.length || 0}
                                </td>
                            ))}
                        <td>
                            <ToggleInput
                                checked={!event.is_closed}
                                disabled={statusSaving}
                                noBottomSpacing
                                label={event.is_closed ? $t('Closed') : $t('Active')}
                                onChange={(e) => {
                                    setStatusSaving(true);
                                    Promise.resolve(setProjectClosed(event, !e.target.checked)).finally(() => setStatusSaving(false));
                                }}
                            />
                        </td>
                        <td>
                            <ToggleInput
                                checked={!!event.rate_card_enabled}
                                disabled={rateCardSaving}
                                noBottomSpacing
                                label={event.rate_card_enabled ? $t('On') : $t('Off')}
                                onChange={(e) => {
                                    setRateCardSaving(true);
                                    Promise.resolve(setRateCardEnabled(event, e.target.checked)).finally(() => setRateCardSaving(false));
                                }}
                            />
                        </td>
                        <td>
                            <TextButton variant="danger" onClick={() => handleDelete(event)}>
                                <TrashIcon className="tw-w-3 tw-h-3" />
                                {$t('Delete')}
                            </TextButton>
                        </td>
                    </Fragment>
                )}
            </tr>
            {currentRole && canViewDetails && <EventUsersRow users={currentUsers} role={currentRole} />}
        </React.Fragment>
    );
}

function EventUsersRow({ users, role }) {
    return (
        <tr className="!tw-bg-white">
            <td colSpan="100%">
                <h4 className="table__subheader tw-mb-2">{role}</h4>
                {users.length ? (
                    <div className="inbud-table_wrapper">
                        <table className="inbud-table">
                            <thead>
                                <tr>
                                    <th>{$t('Name')}</th>
                                    <th className="tw-min-w-44">{$t('email')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((user) => (
                                    <tr key={user.id}>
                                        <td>{user.name}</td>
                                        <td>{user.email}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="tw-text-gray-500">{$t('No users found for this role.')}</div>
                )}
            </td>
        </tr>
    );
}
